import { TelegramAuthClient } from "@bridge-applications/tg-auth";
import {
    ApiAuthAccessTokenResSchema,
    ApiAuthColdSessionReqSchema,
    type ApiAttributionContext,
    ApiAuthAppSessionReqSchema,
    ApiAuthAppSessionResSchema,
    type ApiNetworkContextBootstrapState,
    type ApiSmStateSnapshot,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect } from "react";
import {
    cacheAuthIdentity,
    reconcileAuthIdentity,
    readCachedAuthIdentityForTelegram,
} from "../helpers/authIdentityStorage";
import { browserClientSessionSignalHeaders } from "../helpers/clientSessionSignals";
import {
    browserNetworkContextStorage,
    networkContextRequestHeaders,
} from "../helpers/networkAnalyticsContext";
import { getTelegramWebApp, isTelegramEnv } from "../helpers/telegram";
import {
    getActiveLocale,
    reconcileLocalePreference,
    restoreLocalePreference,
} from "../i18n/localePreference";
import { POSTHOG_ENABLED } from "../../state/config";

interface TelegramAuthBootstrapResult {
    userId?: string;
    telegramUserId?: number;
    accountCreatedAt?: string;
    snapshot?: ApiSmStateSnapshot;
    attributionContext?: ApiAttributionContext;
    networkContextState?: ApiNetworkContextBootstrapState;
}

// initData may be single-use server-side, so the login bootstrap must never
// run twice when StrictMode remounts or routes change.
// One ID per document, reused by StrictMode and refresh-to-login fallback.
const launchId = crypto.randomUUID();

let repairPromise: ReturnType<typeof retryUnavailableAttribution> | null = null;

async function retryUnavailableAttribution() {
    return TelegramAuthClient.getAccessToken(
        ApiAuthAccessTokenResSchema,
        { "X-PB-New-App-Session": "true" },
        {
            schema: ApiAuthColdSessionReqSchema,
            data: {
                launchId,
                telegramInitData: getTelegramWebApp()?.initData || undefined,
            },
        },
    );
}

let bootstrapPromise: Promise<TelegramAuthBootstrapResult> | null = null;

function coldAppSessionHeaders(): Record<string, string> {
    const telegramUserId = getTelegramWebApp()?.initDataUnsafe.user?.id;
    const storage = browserNetworkContextStorage();
    return {
        ...browserClientSessionSignalHeaders(),
        ...(POSTHOG_ENABLED
            ? networkContextRequestHeaders(storage, telegramUserId)
            : {}),
    };
}

async function bootstrap(): Promise<TelegramAuthBootstrapResult> {
    // Token cache state belongs exclusively to tg-auth. The application slice
    // records only whether this app-session bootstrap has finished.
    if (TelegramAuthClient.expectedToBeAuthenticated) {
        try {
            // A fresh Mini App document is a cold app session even when the
            // refresh cookie is still valid. Rotate the token pair explicitly
            // so campaign popup frequency is keyed to this launch, not to an
            // arbitrary access-token expiry.
            const response = await TelegramAuthClient.getAccessToken(
                ApiAuthAccessTokenResSchema,
                {
                    "X-PB-New-App-Session": "true",
                    ...coldAppSessionHeaders(),
                },
                {
                    schema: ApiAuthColdSessionReqSchema,
                    data: {
                        launchId,
                        telegramInitData:
                            getTelegramWebApp()?.initData || undefined,
                    },
                },
            );
            await restoreLocalePreference();
            const cached = readCachedAuthIdentityForTelegram(
                getTelegramWebApp()?.initDataUnsafe.user?.id,
            );
            const identity =
                response.userId && response.telegramUserId
                    ? reconcileAuthIdentity(
                          {
                              userId: response.userId,
                              telegramUserId: response.telegramUserId,
                              accountCreatedAt: response.accountCreatedAt,
                          },
                          cached,
                      )
                    : cached;
            if (identity) cacheAuthIdentity(identity);
            return {
                ...identity,
                networkContextState: response.networkContextState,
                attributionContext: response.attributionContext,
            };
        } catch (error) {
            console.warn(
                "[useTelegramAuth] silent token refresh failed, falling back to login",
                error,
            );
        }
    }

    const response = await TelegramAuthClient.getAppSession<
        typeof ApiAuthAppSessionReqSchema,
        typeof ApiAuthAppSessionResSchema
    >(
        {
            telegramInitData: await TelegramAuthClient.resolveInitData(),
            locale: getActiveLocale(),
            launchId,
        },
        ApiAuthAppSessionReqSchema,
        ApiAuthAppSessionResSchema,
        coldAppSessionHeaders(),
    );
    const identity = reconcileAuthIdentity(
        {
            userId: response.userId,
            telegramUserId: response.telegramUserId,
            accountCreatedAt: response.accountCreatedAt,
        },
        readCachedAuthIdentityForTelegram(response.telegramUserId),
    );
    cacheAuthIdentity(identity);
    await reconcileLocalePreference(response.locale);

    return {
        ...identity,
        snapshot: response.snapshot,
        networkContextState: response.networkContextState,
        attributionContext: response.attributionContext,
    };
}

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : String(error);
}

/**
 * Bootstraps authentication once and writes only app-owned bootstrap metadata
 * to authSlice. Tokens, expiries, and authentication-cache truth stay in
 * TelegramAuthClient; the initial gameplay snapshot is written to its domain
 * slice instead of being duplicated in auth state.
 */
export function useTelegramAuth(): void {
    const { state: authState, setSlice: setAuthSlice } = useSlice("authSlice");
    const { setSlice: setSlotMachineSlice } = useSlice("slotMachineSlice");

    useEffect(() => {
        const context = authState.attributionContext;
        const userId = authState.userId;
        if (
            authState.bootstrapStatus !== "ready" ||
            context?.status !== "unavailable" ||
            !userId
        )
            return;
        let cancelled = false;
        const timer = window.setTimeout(() => {
            repairPromise ??= retryUnavailableAttribution();
            void repairPromise
                .then((response) => {
                    if (
                        cancelled ||
                        response.userId !== userId ||
                        response.attributionContext?.status !== "resolved" ||
                        response.attributionContext.appSessionId !==
                            context.appSessionId
                    )
                        return;
                    setAuthSlice((previous) =>
                        previous.userId === userId &&
                        previous.attributionContext?.appSessionId ===
                            context.appSessionId
                            ? {
                                  attributionContext:
                                      response.attributionContext!,
                              }
                            : {},
                    );
                })
                .catch(() => {
                    /* Keep explicit unavailable context; no polling loop. */
                });
        }, 1000);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        authState.bootstrapStatus,
        authState.userId,
        authState.attributionContext,
        setAuthSlice,
    ]);

    useEffect(() => {
        if (!isTelegramEnv() && !TelegramAuthClient.hasInitDataProvider) {
            if (authState.bootstrapStatus !== "no-telegram") {
                setAuthSlice({
                    bootstrapStatus: "no-telegram",
                    careerAccess: null,
                    error: null,
                });
            }
            return;
        }

        if (
            authState.bootstrapStatus === "ready" ||
            authState.bootstrapStatus === "error"
        ) {
            return;
        }

        if (authState.bootstrapStatus === "idle") {
            setAuthSlice({
                bootstrapStatus: "authenticating",
                careerAccess: null,
                error: null,
            });
        }

        bootstrapPromise ??= bootstrap();

        let cancelled = false;
        bootstrapPromise
            .then((result) => {
                if (cancelled) return;

                if (result.snapshot) {
                    setSlotMachineSlice({
                        snapshot: result.snapshot,
                        loadStatus: "ready",
                        loadError: null,
                    });
                }
                setAuthSlice((previous) => ({
                    sessionGeneration: previous.sessionGeneration + 1,
                    bootstrapStatus: "ready",
                    userId: result.userId ?? null,
                    telegramUserId: result.telegramUserId ?? null,
                    accountCreatedAt: result.accountCreatedAt ?? null,
                    networkContextState: result.networkContextState ?? null,
                    attributionContext: result.attributionContext ?? null,
                    careerAccess: null,
                    error: null,
                }));
            })
            .catch((error: unknown) => {
                console.error("[useTelegramAuth] login failed", error);
                bootstrapPromise = null;
                if (cancelled) return;
                setAuthSlice({
                    bootstrapStatus: "error",
                    careerAccess: null,
                    error: errorMessage(error),
                });
            });

        return () => {
            cancelled = true;
        };
    }, [authState.bootstrapStatus, setAuthSlice, setSlotMachineSlice]);
}
